import {Node} from 'gojs';

import {getDecomposition} from 'src/api/dependency';
import {getNodesInGroup} from 'src/api/groups';
import {
  colorTag,
  mapContainedNodesToNodes,
  mapContainedNodesToEdges,
  mapReferencedNodesToNodes,
  mapReferencedNodesToEdges,
} from 'src/state/dependency2';

// 1. HasNodeParent: Checks if the current node already has been *clicked* / children have been rendered
// If not, load the node's children from API
// Insert into tree
// Re-derive tree structure
export const fetchDataForNode = async (
    node: Node,
    getColorTag?: typeof colorTag,
) => {
  const data = node.data;
  const id = data?.key;

  const {nodes, edges} = node.data.isGroupNode ?
       await fetchNodesInGroup(id, getColorTag) : await fetchNodeDecomposition(id, getColorTag);

  const map: {[k: string]: boolean} = {};
  if (node.diagram) {
    node.diagram.nodes.each(({key}) => {
      if (key) {
        map[key] = true;
      }
    });
  }

  const untrackedNodes = nodes.filter((n) => !map[n.key]);

  return {
    nodes: untrackedNodes,
    edges,
  };
};

export const fetchNodesInGroup = async (
    groupNodeId: string,
    getColorTag?: typeof colorTag,
) => {
  const nodesData = await getNodesInGroup(groupNodeId, {page: 0, pageSize: 1000});
  if (nodesData.error) {
    throw nodesData.error;
  }

  const nodes = nodesData.data.map(({
    id,
    type,
    identity,
    name,
    properties,
  }) => ({
    metadata: {
      type,
      identity,
    },
    text: name || identity,
    name,
    color: getColorTag ? getColorTag(type).color : colorTag(type).color,
    key: id,
    id,
    isGroupNode: false,
    isScanRoot: properties.isScanRoot,
    category: 'parent',
    properties,
  }));

  const edges = nodesData.data.map(({id}: {id: string}) => ({
    from: groupNodeId,
    to: id,
    key: id,
    groups: true,
    color: getColorTag ? getColorTag('*' + 'GROUPS', 'edge').color : colorTag('*' + 'GROUPS', 'edge').color,
  }));

  return {nodes, edges};
};

export const fetchNodeDecomposition = async (
    nodeId: string,
    getColorTag?: typeof colorTag,
) => {
  const res = await getDecomposition(nodeId, undefined, undefined);
  if (res.error) {
    throw res.error;
  }
  const baseNodeId = res.data.id;

  const containedNodes = mapContainedNodesToNodes(res.data.containedNodes, getColorTag);
  const referencedNodes = mapReferencedNodesToNodes(res.data.referencedNodes, getColorTag);

  const nodes = [...containedNodes, ...referencedNodes];

  const containedEdges = mapContainedNodesToEdges(res.data.containedNodes, baseNodeId, getColorTag);
  const referencedEdges = mapReferencedNodesToEdges(res.data.referencedNodes, baseNodeId, getColorTag);

  const edges = [...containedEdges, ...referencedEdges];

  return {nodes, edges};
};

